import { addMoney, toMoney, type Money } from './money.ts'

/**
 * ══ BÁO CÁO TÀI CHÍNH (TT200) — DANH MỤC CHỈ TIÊU và LUẬT GỘP KỲ ═════════════════════════════
 * Chép `BCTC_GROUPS` (`proship-trans/src/pages/AzDashboard.tsx:141`).
 *
 * ⚠ ĐẶT Ở `@rs/contracts` chứ không ở miền kế toán, và đó là CHỦ Ý: danh mục mã chỉ tiêu là một
 *   phần HỢP ĐỒNG API (nó quyết định khoá nào hợp lệ trong `values`), và **cả màn lẫn máy chủ đều
 *   cần nó** — màn để vẽ bảng, máy chủ để từ chối mã lạ. `apps/web` không được import miền
 *   (ranh giới `dependency-cruiser`), nên để ở miền là buộc phải chép sang màn một bản thứ hai.
 *
 * ⭐ SỐ Ở ĐÂY KHÔNG PHẢI rs-erp TỰ TÍNH. Kế toán chốt B01/B02/B03 trên phần mềm kế toán rồi xuất
 *   Excel; miền này chỉ giữ lại và gộp kỳ. Tự tính lại là dựng một bộ sổ thứ hai cạnh bộ sổ thật.
 *
 * ⭐ GỘP KỲ CÓ BA LUẬT, KHÔNG PHẢI MỘT — và đây là toàn bộ cái khó của tệp này:
 *   · `sum`   — chỉ tiêu LUỒNG (doanh thu, chi phí, lãi): quý = cộng ba tháng.
 *   · `last`  — chỉ tiêu SỐ DƯ (tài sản, nợ, vốn): quý = số dư THÁNG CUỐI, cộng lại là vô nghĩa.
 *   · `first` — tiền đầu kỳ: quý = số ĐẦU tháng đầu tiên.
 *   Dùng nhầm `sum` cho một dòng cân đối kế toán là bày ra con số gấp ba lần tổng tài sản thật.
 */

export type BctcKind = 'sum' | 'last' | 'first'
export type BctcStmt = 'B01' | 'B02' | 'B03'

export interface BctcItem {
  code: string
  label: string
  bold?: boolean
  /** Luật gộp RIÊNG của dòng, đè luật của nhóm. Chỉ hai dòng tiền đầu/cuối kỳ dùng tới. */
  kind?: BctcKind
}

export interface BctcGroup {
  group: string
  stmt: BctcStmt
  kind: BctcKind
  items: BctcItem[]
}

/** Chép NGUYÊN VĂN danh mục và nhãn của bản cũ — kể cả số hiệu mã trong ngoặc. */
export const BCTC_GROUPS: readonly BctcGroup[] = Object.freeze([
  {
    group: 'KẾT QUẢ KINH DOANH (B02-DN)', stmt: 'B02', kind: 'sum', items: [
      { code: 'B02.01', label: 'DT bán hàng & cung cấp DV (01)' },
      { code: 'B02.02', label: 'Các khoản giảm trừ doanh thu (02)' },
      { code: 'B02.10', label: 'Doanh thu thuần (10)', bold: true },
      { code: 'B02.11', label: 'Giá vốn hàng bán (11)' },
      { code: 'B02.20', label: 'Lợi nhuận gộp (20)', bold: true },
      { code: 'B02.21', label: 'Doanh thu tài chính (21)' },
      { code: 'B02.22', label: 'Chi phí tài chính (22)' },
      { code: 'B02.23', label: '— trong đó: lãi vay (23)' },
      { code: 'B02.25', label: 'Chi phí bán hàng (25)' },
      { code: 'B02.26', label: 'Chi phí quản lý DN (26)' },
      { code: 'B02.30', label: 'LN thuần từ HĐKD (30)', bold: true },
      { code: 'B02.31', label: 'Thu nhập khác (31)' },
      { code: 'B02.32', label: 'Chi phí khác (32)' },
      { code: 'B02.40', label: 'Lợi nhuận khác (40)' },
      { code: 'B02.50', label: 'LN trước thuế (50)', bold: true },
      { code: 'B02.51', label: 'Thuế TNDN hiện hành (51)' },
      { code: 'B02.52', label: 'Thuế TNDN hoãn lại (52)' },
      { code: 'B02.60', label: 'LN sau thuế (60)', bold: true },
    ],
  },
  {
    group: 'CÂN ĐỐI KẾ TOÁN (B01-DN — số dư cuối kỳ)', stmt: 'B01', kind: 'last', items: [
      { code: 'B01.100', label: 'Tài sản ngắn hạn (100)', bold: true },
      { code: 'B01.110', label: 'Tiền & tương đương tiền (110)' },
      { code: 'B01.130', label: 'Phải thu ngắn hạn (130)' },
      { code: 'B01.131', label: '— phải thu khách hàng (131)' },
      { code: 'B01.140', label: 'Hàng tồn kho (140)' },
      { code: 'B01.200', label: 'Tài sản dài hạn (200)', bold: true },
      { code: 'B01.220', label: 'Tài sản cố định (220)' },
      { code: 'B01.270', label: 'TỔNG CỘNG TÀI SẢN (270)', bold: true },
      { code: 'B01.300', label: 'Nợ phải trả (300)', bold: true },
      { code: 'B01.310', label: 'Nợ ngắn hạn (310)' },
      { code: 'B01.320', label: '— vay & nợ thuê TC ngắn hạn (320)' },
      { code: 'B01.330', label: 'Nợ dài hạn (330)' },
      { code: 'B01.338', label: '— vay & nợ thuê TC dài hạn (338)' },
      { code: 'B01.400', label: 'Vốn chủ sở hữu (400)', bold: true },
      { code: 'B01.440', label: 'TỔNG CỘNG NGUỒN VỐN (440)', bold: true },
    ],
  },
  {
    group: 'LƯU CHUYỂN TIỀN TỆ (B03-DN)', stmt: 'B03', kind: 'sum', items: [
      { code: 'B03.20', label: 'LC thuần từ HĐ kinh doanh (20)', bold: true },
      { code: 'B03.30', label: 'LC thuần từ HĐ đầu tư (30)', bold: true },
      { code: 'B03.40', label: 'LC thuần từ HĐ tài chính (40)', bold: true },
      { code: 'B03.50', label: 'LC tiền thuần trong kỳ (50)' },
      { code: 'B03.60', label: 'Tiền & tương đương ĐẦU kỳ (60)', kind: 'first' },
      { code: 'B03.70', label: 'Tiền & tương đương CUỐI kỳ (70)', bold: true, kind: 'last' },
    ],
  },
])

const ALL_ITEMS: readonly (BctcItem & { stmt: BctcStmt; groupKind: BctcKind })[] = Object.freeze(
  BCTC_GROUPS.flatMap((g) => g.items.map((row) => ({ ...row, stmt: g.stmt, groupKind: g.kind }))))

/** Mọi mã hợp lệ. Một mã ngoài danh sách là một dòng không có chỗ hiện ra. */
export const BCTC_CODES: ReadonlySet<string> = Object.freeze(
  new Set(ALL_ITEMS.map((row) => row.code)))

export const bctcLabelOf = (code: string): string =>
  ALL_ITEMS.find((row) => row.code === code)?.label ?? code

/** Luật gộp của MỘT mã: luật riêng của dòng thắng luật của nhóm. */
export const bctcKindOf = (code: string): BctcKind => {
  const row = ALL_ITEMS.find((x) => x.code === code)
  return row === undefined ? 'sum' : (row.kind ?? row.groupKind)
}

/**
 * Mã chỉ tiêu KQKD được đặt KẾ HOẠCH NĂM (user 21/08, theo bảng mẫu phần mềm kế toán đưa).
 * Chỉ mười mã — không phải mọi dòng của B02 đều có kế hoạch.
 */
export const BCTC_TARGET_CODES: readonly string[] = Object.freeze([
  'B02.01', 'B02.02', 'B02.10', 'B02.11', 'B02.20',
  'B02.22', 'B02.25', 'B02.26', 'B02.30', 'B02.50',
])

/** Một kỳ đã nhập: `YYYY-MM` và giá trị từng mã (chuỗi thập phân, §2). */
export interface BctcPeriodRow {
  id: string
  period: string
  values: Record<string, Money>
  fileName: string | null
  importedBy: string | null
  importedAt: string
  version: number
}

/**
 * GỘP một mã qua nhiều tháng.
 *
 * ⚠ `months` PHẢI đã sắp tăng dần — `first`/`last` đọc hai đầu của mảng, nên thứ tự LÀ nghĩa.
 * ⚠ `null` khi KHÔNG THÁNG NÀO có số cho mã đó: khác hẳn 0. Bảng hiện dấu gạch, không hiện "0 đ" —
 *   một chỉ tiêu chưa nhập mà bày ra số 0 là nói rằng doanh nghiệp không có doanh thu.
 */
export const rollup = (
  rows: readonly BctcPeriodRow[], code: string, months: readonly string[],
): Money | null => {
  const kind = bctcKindOf(code)
  const byPeriod = new Map(rows.map((r) => [r.period, r]))
  const hits = months
    .map((m) => byPeriod.get(m)?.values[code])
    .filter((v): v is Money => v !== undefined)
  if (hits.length === 0) return null
  if (kind === 'first') return hits[0] as Money
  if (kind === 'last') return hits[hits.length - 1] as Money
  return hits.reduce<Money>((s, v) => addMoney(s, v), toMoney(0))
}

/** Mười hai tháng của một năm, tăng dần. */
export const monthsOfYear = (year: number): string[] =>
  Array.from({ length: 12 }, (_, i) => `${String(year)}-${String(i + 1).padStart(2, '0')}`)

/** Ba tháng của một quý, tăng dần. */
export const monthsOfQuarter = (year: number, q: number): string[] =>
  [0, 1, 2].map((i) => `${String(year)}-${String((q - 1) * 3 + i + 1).padStart(2, '0')}`)

/** Luỹ kế từ tháng 1 tới hết tháng `m`. */
export const monthsToDate = (year: number, m: number): string[] =>
  monthsOfYear(year).slice(0, m)

/**
 * %CHÊNH giữa kỳ này và kỳ so sánh.
 *
 * ⚠ MẪU SỐ LÀ TRỊ TUYỆT ĐỐI. Kỳ trước LỖ (số âm) mà kỳ này lỗ ít hơn thì đó là TIẾN BỘ; chia cho
 *   một số âm sẽ đảo dấu và bày ra mũi tên đỏ cho một chuyện tốt. Chép đúng `pctDelta` bản cũ.
 * ⚠ `null` khi thiếu một trong hai vế, hoặc khi kỳ gốc bằng 0 — không có gốc thì không có phần trăm.
 */
export const pctDelta = (cur: Money | null, base: Money | null): number | null => {
  if (cur === null || base === null) return null
  const b = Number(base)
  if (b === 0) return null
  return Math.round((Number(cur) - b) / Math.abs(b) * 100)
}

/**
 * ⭐ KỲ NHẬP BCTC: tháng `YYYY-MM` · quý `YYYY-Qn` · `YYYY-6T` · `YYYY-9T` · năm `YYYY-NAM` — bản cũ `bctcKey`
 * (`AzDashboard.tsx:1179-1180`): nút 📥 Nhập kỳ nhập ĐÚNG kỳ đang so sánh (`setImpKind(cmpKind)`, `:4403`). Kế toán chỉ có BCTC
 * quý/năm từ MISA thì nhập thẳng kỳ đó (V03548 · V03549 · V03582 · V03956). Trước 24/10 chỉ nhận kỳ tháng.
 */
export const BCTC_PERIOD_RE = /^\d{4}-((0[1-9]|1[0-2])|Q[1-4]|6T|9T|NAM)$/

export type BctcPeriodKind = 'M' | 'Q' | '6T' | '9T' | 'Y'

/** Khoá kỳ lưu trong CSDL — bản cũ `bctcKey`. */
export const bctcPeriodKey = (kind: BctcPeriodKind, year: number, part: number): string => {
  const y = String(year)
  return kind === 'M' ? `${y}-${String(part).padStart(2, '0')}`
    : kind === 'Q' ? `${y}-Q${String(part)}`
      : kind === '6T' ? `${y}-6T` : kind === '9T' ? `${y}-9T` : `${y}-NAM`
}

/** Gộp một dãy giá trị theo luật của mã (`bctcAgg` bản cũ): bỏ ô trống; không còn gì ⇒ `null`. */
const aggOf = (code: string, parts: readonly (Money | null)[]): Money | null => {
  const have = parts.filter((v): v is Money => v !== null)
  if (have.length === 0) return null
  const kind = bctcKindOf(code)
  if (kind === 'first') return have[0] as Money
  if (kind === 'last') return have[have.length - 1] as Money
  return have.reduce<Money>((s, v) => addMoney(s, v), toMoney(0))
}

/**
 * ⭐ GIÁ TRỊ MỘT KỲ theo thứ tự ưu tiên của bản cũ (`bctcVal` · `bctcQuarterVal` · `bctcSpanVal`, `AzDashboard.tsx:1189-1201`):
 * · Tháng = số nhập của tháng đó.
 * · Quý = số nhập TRỰC TIẾP cho quý, chưa có thì gộp ba tháng.
 * · 6T / 9T / Năm = số nhập trực tiếp cho kỳ đó, chưa có thì gộp các tháng, vẫn chưa có thì gộp các quý.
 * `rows` của MỘT năm (máy chủ trả theo năm).
 */
export const bctcValue = (
  rows: readonly BctcPeriodRow[], code: string, kind: BctcPeriodKind, year: number, part: number,
): Money | null => {
  const direct = (key: string): Money | null => rows.find((r) => r.period === key)?.values[code] ?? null
  if (kind === 'M') return direct(bctcPeriodKey('M', year, part))
  const quarter = (q: number): Money | null => direct(bctcPeriodKey('Q', year, q)) ?? rollup(rows, code, monthsOfQuarter(year, q))
  if (kind === 'Q') return quarter(part)
  const upTo = kind === '6T' ? 6 : kind === '9T' ? 9 : 12
  return direct(bctcPeriodKey(kind, year, part))
    ?? rollup(rows, code, monthsToDate(year, upTo))
    ?? aggOf(code, Array.from({ length: upTo / 3 }, (_, i) => quarter(i + 1)))
}

/** Câu từ chối khi một kỳ nhập vào không hợp lệ — `null` nghĩa là hợp lệ. */
export const bctcPeriodError = (
  p: { period: string; values: Record<string, unknown> },
): string | null => {
  if (!BCTC_PERIOD_RE.test(p.period)) return 'Kỳ phải có dạng YYYY-MM, YYYY-Qn, YYYY-6T, YYYY-9T hoặc YYYY-NAM.'
  const unknown = Object.keys(p.values).filter((c) => !BCTC_CODES.has(c))
  if (unknown.length > 0) {
    return `Mã chỉ tiêu không có trong danh mục: ${unknown.slice(0, 5).join(', ')}.`
  }
  return null
}
